/**
 * Banner displayed when sysadmin is emulating a user.
 *
 * Shows a compact, centered warning pill with emulated user info and end button.
 * Positioned at top center so it doesn't cover navigation elements.
 */
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useEmulation } from '../contexts/EmulationContext';
import { cn } from '../../lib/utils';

export function EmulationBanner() {
  const { t } = useTranslation('admin');
  const { emulationSession, isEmulating, endEmulation, isEmulationExpired } = useEmulation();

  if (!isEmulating || !emulationSession) {
    return null;
  }

  const expired = isEmulationExpired();
  const expiresAt = new Date(emulationSession.expires_at);
  const now = new Date();
  const minutesRemaining = Math.max(0, Math.round((expiresAt.getTime() - now.getTime()) / 60000));

  const handleEndEmulation = async () => {
    await endEmulation();
  };

  return (
    <div
      className={cn(
        'fixed top-2 left-1/2 -translate-x-1/2 z-[1300]',
        'flex items-center gap-3',
        'py-1.5 px-4 rounded-lg shadow-lg',
        expired ? 'bg-destructive text-destructive-foreground' : 'bg-amber-500 text-amber-950'
      )}
    >
      <AlertTriangle className="h-4 w-4" />
      <span className="text-sm font-medium whitespace-nowrap">
        {emulationSession.emulated_user_name}
      </span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="secondary"
              className="bg-black/15 text-inherit h-5 text-xs"
            >
              {emulationSession.emulated_tenant_name}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>{emulationSession.emulated_user_email} @ {emulationSession.emulated_tenant_name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {!expired && (
        <span className="text-xs opacity-85">
          {t('emulation.banner.minutesRemaining', { minutes: minutesRemaining })}
        </span>
      )}
      {expired && (
        <span className="text-xs font-bold">
          {t('emulation.banner.expired')}
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleEndEmulation}
        className="text-inherit py-0 px-2 h-auto text-xs hover:bg-black/10"
      >
        {t('emulation.banner.endEmulation')}
      </Button>
    </div>
  );
}
